import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { isEqual } from 'lodash'

import { Button, CircularProgress, Stack, TextField, Box } from '@mui/material'
import ToastAlert from '../../../../core/Layouts/ToastAlert'
import SideDialog from '../../../dis/components/SideDialog'
import PlottingMapForm from './PlottingMapForm'
import { TASK_MAP_SETTING_TYPES } from '../summaries/utils/constant'

export function PlottingMapSettingDialog(props) {
	const {
		tileElementWidth,
		defaultTileWidth,
		tile,
		open,
		settings,
		isSubmitting,
		showStable,
		onClose,
		onSubmit,
		onResizeTileWidth,
		showInModal,
	} = props

	const { t } = useTranslation('common')
	const { id: dashboardKey } = useParams()

	const [alertInfo, setAlertInfo] = useState({
		open: false,
		text: '',
		type: 'success',
	})

	const [filterData, setFilterData] = useState({})

	const dialogWidth = tile?.w ?? 6
	const STABLE_WIDTH = 6

	useEffect(() => {
		if (isEqual(settings?.selectedTaskMapForm, filterData?.selectedTaskMapForm)) return
		if (settings?.selectedTaskMapForm) {
			setFilterData((prev) => ({ ...prev, selectedTaskMapForm: settings?.selectedTaskMapForm }))
		}
	}, [settings?.selectedTaskMapForm])

	useEffect(() => {
		if (isEqual(settings?.taskMapAddressField, filterData?.taskMapAddressField)) return
		if (settings?.taskMapAddressField) {
			setFilterData((prev) => ({ ...prev, taskMapAddressField: settings?.taskMapAddressField }))
		}
	}, [settings?.taskMapAddressField])

	useEffect(() => {
		if (isEqual(settings?.taskMapDatePreset, filterData?.taskMapDatePreset)) return
		if (settings?.taskMapDatePreset) {
			setFilterData((prev) => ({ ...prev, taskMapDatePreset: settings?.taskMapDatePreset }))
		}
	}, [settings?.taskMapDatePreset])

	useEffect(() => {
		if (isEqual(settings?.taskMapBubbleFields, filterData?.taskMapBubbleFields)) return
		if (settings?.taskMapBubbleFields) {
			setFilterData((prev) => ({ ...prev, taskMapBubbleFields: settings?.taskMapBubbleFields }))
		}
	}, [settings?.taskMapBubbleFields])

	useEffect(() => {
		if (isEqual(settings?.primary, filterData?.primaryKeyList)) return
		if (settings?.primaryKeyList) {
			setFilterData((prev) => ({ ...prev, primaryKeyList: settings?.primaryKeyList }))
		}
	}, [settings?.primaryKeyList])

	useEffect(() => {
		if (isEqual(settings?.columnsChecked, filterData?.columnsChecked)) return
		if (settings?.columnsChecked) {
			setFilterData((prev) => ({ ...prev, columnsChecked: settings?.columnsChecked }))
		}
	}, [settings?.columnsChecked])

	useEffect(() => {
		if (isEqual(settings?.otherOptionsChecked, filterData?.otherOptionsChecked)) return
		if (settings?.otherOptionsChecked) {
			setFilterData((prev) => ({ ...prev, otherOptionsChecked: settings?.otherOptionsChecked }))
		}
	}, [settings?.otherOptionsChecked])

	useEffect(() => {
		if (isEqual(tile?.type, filterData?.type)) return
		if (tile?.type) {
			setFilterData((prev) => ({ ...prev, type: tile?.type }))
		}
	}, [tile?.type])

	useEffect(() => {
		if (isEqual(settings?.migrateOtherOptions, filterData?.migrateOtherOptions)) return
		if (settings?.migrateOtherOptions) {
			setFilterData((prev) => ({ ...prev, migrateOtherOptions: settings?.migrateOtherOptions }))
		}
	}, [settings?.migrateOtherOptions])

	const handleSubmit = () => {
		if (!filterData || !filterData?.selectedTaskMapForm) {
			setAlertInfo((prev) => ({
				...prev,
				open: true,
				type: 'error',
				text: t('common:dis.pleaseFillInformation'),
			}))
			return
		}

		onSubmit?.({
			selectedTaskMapForm: filterData?.selectedTaskMapForm,
			taskMapAddressField: filterData?.taskMapAddressField,
			taskMapDatePreset: filterData?.taskMapDatePreset,
			taskMapBubbleFields: filterData?.taskMapBubbleFields,
			primaryKeyList: filterData?.primaryKeyList,
			columnsChecked: filterData?.columnsChecked,
			otherOptionsChecked: filterData?.otherOptionsChecked,
			migrateOtherOptions: filterData?.migrateOtherOptions,
		})
		onClose()
	}

	const handleFilterDataChange = (id, value) => {
		setFilterData((prev) => ({ ...prev, [id]: value }))
	}
	return (
		<>
			<ToastAlert alertInfo={alertInfo} setAlertInfo={setAlertInfo} />

			<SideDialog
				open={open}
				onClose={onClose}
				width={showStable ? STABLE_WIDTH : dialogWidth}
				tileElementWidth={tileElementWidth}
				onResizeWidth={onResizeTileWidth}
				defaultTileWidth={defaultTileWidth}
				showInModal={showInModal}
			>
				<Stack
					sx={{
						'& .MuiInputBase-input': {
							fontSize: '0.9rem !important',
						},
						'& .MuiCheckbox-root': {
							p: 0,
						},
					}}
				>
					<Box sx={{ px: 1, mb: '15px' }}>
						<TextField
							fullWidth
							id="label-basic"
							label={t('common:chart.labels')}
							variant="standard"
							value={tile.i}
							disabled={true}
							size="small"
						/>
					</Box>

					<PlottingMapForm
						filterData={filterData}
						handleFilterDataChange={handleFilterDataChange}
						taskMapSettingType={TASK_MAP_SETTING_TYPES.ALL}
						tileType={tile?.type}
					/>

					<Stack direction="row" justifyContent="flex-end" mt={2}>
						<Stack spacing={2} direction="row">
							<Button
								size="small"
								variant="contained"
								onClick={handleSubmit}
								disabled={isSubmitting}
								startIcon={isSubmitting ? <CircularProgress color="inherit" size="1em" /> : null}
							>
								{t('common:misc.ok')}
							</Button>
							<Button size="small" variant="text" onClick={onClose}>
								{t('common:misc.cancel')}
							</Button>
						</Stack>
					</Stack>
				</Stack>
			</SideDialog>
		</>
	)
}
